/**
 * Node Type Definitions
 * Based on Process_ProcessElements schema
 */

import { Node } from 'reactflow';

export type NodeState = 'new' | 'existing' | 'modified' | 'deleted';

export type ExecutionMode =
  | 'always'
  | 'once'
  | 'trigger'
  | 'manual'
  | 'conditional'
  | 'scheduled'
  | 'onError';

// Kept in sync with the real runtime values written by
// useNodeExecutionSync.ts ('running' | 'success' | 'failed' | 'skipped' |
// 'waiting' | 'retrying' | 'timeout'); 'error' is retained for back-compat
// with older node-renderer checks.
export type ExecutionStatus =
  | 'idle'
  | 'running'
  | 'success'
  | 'error'
  | 'failed'
  | 'skipped'
  | 'waiting'
  | 'retrying'
  | 'timeout';

// ─── Port ─────────────────────────────────────────────────────────────────────

export interface ProcessElementPort {
  processElementPortID?: number;
  processElementID?: number;
  portKey: string;
  portType: 'input' | 'output';
  portIndex: number;
  displayName?: string;
  label?: string;
  description?: string;
  isRequired: boolean;
  isMultiple: boolean;
  isMainPort: boolean;
  isErrorPort: boolean;
  isDynamic?: boolean;
  isCustom?: boolean;
  isSatellite?: boolean;
  satellite?: { satellite?: string };
  color?: string;
  iconUrl?: string;
  position: 'top' | 'right' | 'bottom' | 'left';
  handleStyle: 'circle' | 'square' | 'diamond' | 'arrow' | 'triangle';
  offsetX?: number;
  offsetY?: number;
  labelOffsetX?: number;
  labelOffsetY?: number;
  helpText?: string;
  dataType?: string;
}

// ─── System ports (injected into every node at load time) ────────────────────

/** Output port on any parent node — left side, near bottom. Connects TO a component node. */
export const SYSTEM_MY_COMPONENTS_PORT: ProcessElementPort = {
  portKey: 'my-components',
  portType: 'output',
  portIndex: 9000,
  isRequired: false,
  isMultiple: true,
  isMainPort: false,
  isErrorPort: false,
  position: 'left',
  handleStyle: 'diamond',
  color: '#8b5cf6',
  offsetX: -2,
  offsetY: 50,
  labelOffsetY: 0,
  displayName: 'My Components',
  label: 'My Components',
};

/** Input port on any node acting as a component — top side, far right. Receives FROM parent. */
export const SYSTEM_COMPONENT_PORT: ProcessElementPort = {
  portKey: 'component',
  portType: 'input',
  portIndex: 9001,
  isRequired: false,
  isMultiple: true,
  isMainPort: false,
  isErrorPort: false,
  position: 'right',
  handleStyle: 'diamond',
  color: '#8b5cf6',
  offsetX: 2,
  offsetY: -70,
  labelOffsetY: 0,
  displayName: 'Component',
  label: 'Component',
};

// ─── Configuration ────────────────────────────────────────────────────────────

export interface ProcessElementConfiguration {
  designer?: {
    ui?: {
      color?: string;
      iconUrl?: string;
      icon?: string;
      iconStyle?: string;
      nodeStyle?: string;
      category?: string;
      nodeType?: string;
      dataTemplateID?: number;
      triggerType?: string;
      nodeCss?: string;
      style?: Record<string, any>; // full StyleProperties stored from StyleBuilderPanel
      headerStyle?: Record<string, any>; // full StyleProperties for the node header
      headerImageUrl?: string;
      bodyImageUrl?: string;
      helpUrl?: string;
      width?: number;
      height?: number;
    };
  };
  // Form values entered by the user in the node's Atlas Form configuration panels.
  // Persisted via PS_ProcessElements.Configuration through the workflow save.
  nodeFormValues?: Record<string, any>;
}

export interface ConnectorConfiguration {
  [key: string]: any;
}

// ─── Connector ────────────────────────────────────────────────────────────────

export interface ConnectorData {
  connectorID?: number;
  extensionID?: number;
  name?: string;
  description?: string;
  baseUrl?: string;
  apiEndPoint?: string;
  timeout?: number;
  retryCount?: number;
  credentialID?: number;
  agentID?: number;
  knowledgeBaseID?: number;
  channelID?: number;
  llmModelID?: number;
  agentFunctionID?: number;
  configuration: ConnectorConfiguration; // always a parsed object in state
  isActive?: boolean;
}

// ─── ProcessElement ───────────────────────────────────────────────────────────

export interface ProcessElementData {
  // Identity (mirrors Process_ProcessElements DB columns)
  processElementID?: number;
  processElementKey: string;
  processThreadVersionID?: number;
  processElementTypeID?: number;
  extensionID?: number;

  // Core
  name: string;
  displayName: string;
  description?: string;

  // Behavior
  isTrigger: boolean;
  isDisabled: boolean;
  continueOnFail: boolean;
  alwaysOutputData: boolean;
  retryOnFail: boolean;
  maxRetries: number;
  retryWaitTime?: number;
  timeout?: number;
  isLocked?: boolean;
  notes?: string;
  pinnedData?: string;

  // Configuration — always a parsed object in state, never a string
  configuration: ProcessElementConfiguration;

  // Ports — always an array, never undefined
  ports: ProcessElementPort[];

  // Connector — always present; connectorID is undefined when not yet linked
  connectorID?: number;
  connector: ConnectorData;

  // Node-type-specific parameters (e.g. variable-assignment: { variable, value })
  parameters?: Record<string, any>;

  // Legacy flat visual fields — fallback only; prefer configuration.designer.ui.*
  color?: string;
  iconUrl?: string;
}

// ─── Runtime ──────────────────────────────────────────────────────────────────

export interface WorkflowNodeRuntime {
  executionStatus: ExecutionStatus;
  executionMode?: ExecutionMode;
  collapsed: boolean;
  lastExecutionTime?: number;
  lastExecutionData?: unknown;
}

// ─── WorkflowNodeData ─────────────────────────────────────────────────────────

export interface WorkflowNodeData {
  processElement?: ProcessElementData;
  runtime?: WorkflowNodeRuntime;
  nodeKey?: string;
  displayName?: string;
  description?: string;
  color?: string;
  iconUrl?: string;
  category?: string;
  isTrigger?: boolean;
  isDisabled?: boolean;
  continueOnFail?: boolean;
  alwaysOutputData?: boolean;
  retryOnFail?: boolean;
  parameters?: Record<string, any>;
  inputPorts?: ProcessElementPort[];
  outputPorts?: ProcessElementPort[];
  executionMode?: string;
  executionStatus?: string;
  collapsed?: boolean;
  connectorId?: number;
  credentialId?: string;
  maxRetries?: number;
  timeout?: number;
  retryWaitTime?: number;
  subWorkflowId?: string;
  groupId?: string;
  isSatellite?: boolean;
  satellite?: { satellite?: string };
}

// ─── Display helper ──────────────────────────────────────────────────────────

export interface NodeDisplayProps {
  displayName: string;
  description?: string;
  color: string;
  iconUrl: string;
  icon?: string;
  /** JSON-encoded StyleProperties for the node icon — parse before passing to BizFirstIcon */
  iconStyle?: string;
  nodeStyle?: string;
  headerStyle?: Record<string, any>;
  category?: string;
  isTrigger: boolean;
  isDisabled: boolean;
  connectorID?: number;
  connector: ConnectorData;
  inputPorts: ProcessElementPort[];
  outputPorts: ProcessElementPort[];
  collapsed: boolean;
  triggerType?: string;
  executionStatus: ExecutionStatus;
  processElementID?: number;
  notes?: string;
  headerImageUrl?: string;
  bodyImageUrl?: string;
  helpUrl?: string;
}

export function getNodeDisplayProps(data: WorkflowNodeData): NodeDisplayProps {
  // Guard against legacy flat-structure nodes that pre-date the processElement schema
  const pe = data?.processElement ?? (data as any);
  const ui = pe?.configuration?.designer?.ui ?? {};
  return {
    displayName: pe?.displayName ?? '',
    description: pe?.description,
    color: ui.color ?? pe?.color ?? '#309B48',
    iconUrl: ui.iconUrl ?? pe?.iconUrl ?? '',
    icon: ui.icon,
    iconStyle: ui.iconStyle,
    nodeStyle: ui.nodeStyle,
    headerStyle: ui.headerStyle,
    category: ui.category,
    isTrigger: pe?.isTrigger ?? false,
    isDisabled: pe?.isDisabled ?? false,
    connectorID: pe?.connectorID,
    connector: pe?.connector ?? { configuration: {} },
    inputPorts: (pe?.ports ?? []).filter((p: any) => p.portType === 'input'),
    outputPorts: (pe?.ports ?? []).filter((p: any) => p.portType === 'output'),
    collapsed: data?.runtime?.collapsed ?? false,
    triggerType: ui.triggerType,
    executionStatus: data?.runtime?.executionStatus ?? 'idle',
    processElementID: pe?.processElementID,
    notes: pe?.notes,
    headerImageUrl: ui.headerImageUrl,
    bodyImageUrl: ui.bodyImageUrl,
    helpUrl: ui.helpUrl,
  };
}

export function getInputPorts(pe: ProcessElementData): ProcessElementPort[] {
  return pe.ports.filter((p) => p.portType === 'input');
}

export function getOutputPorts(pe: ProcessElementData): ProcessElementPort[] {
  return pe.ports.filter((p) => p.portType === 'output');
}

// ─── WorkflowNode ─────────────────────────────────────────────────────────────

export interface WorkflowNode extends Node {
  id: string;
  type: string;
  position: { x: number; y: number };
  data: WorkflowNodeData;
  width?: number;
  height?: number;
  selected?: boolean;
  dragging?: boolean;
  zIndex?: number;
  state?: NodeState;

  // Kept alongside data.processElement.processElementID for React Flow compatibility
  processElementId?: number;
  // Kept for save/template tracking
  templateReference?: NodeType;
}

// ─── NodeType ─────────────────────────────────────────────────────────────────

export interface NodeType {
  code: string;
  displayName: string;
  description: string;
  category: string;
  iconUrl?: string;
  icon?: string;
  /** JSON-encoded StyleProperties for the node icon */
  iconStyle?: string;
  color?: string;
  nodeStyle?: string;
  nodeCss?: string;
  extensionId?: number;
  id?: number;
  shortDescription?: string;
  longDescription?: string;
  imageUrl?: string;
  helpUrl?: string;
  configurationSchema?: object;
  inputSchema?: object;
  outputSchema?: object;
  parameterSchema?: object;
  defaultInputPorts: ProcessElementPort[];
  defaultOutputPorts: ProcessElementPort[];
  isAsync: boolean;
  supportsMultipleOutputs: boolean;
  requiresCredentials: boolean;
  acceptedCredentialTypes?: AcceptedCredentialType[]; // zero-config: { code: 'GOOGLE_OAUTH2' }
  isDeprecated?: boolean;
  version?: string;
}

export interface AcceptedCredentialType {
  code: string;        // matches AIExt_CredentialTypes.Code exactly
  editFormID?: number; // optional Atlas Form ID override (rare)
  viewFormID?: number; // optional Atlas Form ID override (rare)
}

// ─── NodeCategory ─────────────────────────────────────────────────────────────

export interface NodeCategory {
  code: string;
  displayName: string;
  description?: string;
  iconUrl?: string;
  icon?: string;
  iconStyle?: string;
  color?: string;
  nodeStyle?: string;
  nodeCss?: string;
  sortOrder?: number;
  shortDescription?: string;
  longDescription?: string;
  imageUrl?: string;
  helpUrl?: string;
  /** Category-wide default credential types — used as fallback when a node declares requiresCredentials but has no acceptedCredentialTypes of its own */
  acceptedCredentialTypes?: AcceptedCredentialType[];
}
